import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import LoadingSpinner from '../../../components/LoadingSpinner'
import MemberBottomNav from '../../../components/MemberBottomNav'
import PullToRefresh from '../../../components/PullToRefresh'
import { 
  Bell,
  CalendarBlank,
  CalendarDots,
} from '@phosphor-icons/react'

export default function MemberStats() {
  const navigate = useNavigate()
  const [userData, setUserData] = useState(null)
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth())
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [monthStats, setMonthStats] = useState({
    confirmed: 0,
    attended: 0,
    missed: 0,
    declined: 0,
    rate: 0,
    total: 0
  })
  const [overallRate, setOverallRate] = useState(0)
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadStats()
  }, [selectedMonth, selectedYear])

  const loadStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      const { data: userDataResult } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single()
      
      setUserData(userDataResult)

      const now = new Date()
      
      // Get month range
      const monthStart = new Date(selectedYear, selectedMonth, 1)
      const monthEnd = new Date(selectedYear, selectedMonth + 1, 0)
      
      const monthStartStr = monthStart.toISOString().split('T')[0]
      const monthEndStr = monthEnd.toISOString().split('T')[0]

      // Get past events only for selected month
      const today = new Date().toISOString().split('T')[0]
      const { data: monthEvents } = await supabase
        .from('events')
        .select('*')
        .eq('org_id', userDataResult.org_id)
        .gte('start_date', monthStartStr)
        .lte('start_date', monthEndStr)
        .lte('start_date', today)  // Only past events
        .order('start_date', { ascending: false })

      // Get PAST events only for overall rate
      const { data: allOrgEvents } = await supabase
        .from('events')
        .select('id')
        .eq('org_id', userDataResult.org_id)
        .lte('start_date', new Date().toISOString().split('T')[0])

      // Get all confirmations
      const { data: allConfirmations } = await supabase
        .from('event_confirmations')
        .select('*')
        .eq('user_id', user.id)

      // Get all attendance records
      const { data: allAttendance } = await supabase
        .from('attendance_records')
        .select('*')
        .eq('user_id', user.id)

      // Calculate overall attendance rate
      const totalAllEvents = allOrgEvents?.length || 0
      const totalAttended = allAttendance?.length || 0
      const overallAttendanceRate = totalAllEvents > 0 ? Math.round((totalAttended / totalAllEvents) * 100) : 0
      setOverallRate(overallAttendanceRate)

      // Process month events with status
      let confirmedCount = 0
      let attendedCount = 0
      let missedCount = 0
      let declinedCount = 0

      const eventsWithStatus = await Promise.all(
        (monthEvents || []).map(async (event) => {
          const eventDate = new Date(`${event.start_date}T${event.start_time}`)
          const isPast = eventDate < now

          // Check attendance
          const attended = allAttendance?.find(a => a.event_id === event.id)
          
          if (attended) {
            attendedCount++
            return { ...event, status: 'attended' }
          }

          // Check confirmation
          const confirmation = allConfirmations?.find(c => c.event_id === event.id)
          
          if (isPast) {
            if (confirmation?.status === 'declined') {
              declinedCount++
              return { ...event, status: 'declined' }
            }
            missedCount++
            return { ...event, status: 'missed' }
          }

          if (confirmation) {
            if (confirmation.status === 'confirmed') {
              confirmedCount++
              return { ...event, status: 'confirmed' }
            }
            if (confirmation.status === 'declined') {
              declinedCount++
              return { ...event, status: 'declined' }
            }
          }

          return { ...event, status: 'no_response' }
        })
      )

      const totalMonthEvents = monthEvents?.length || 0
      const monthRate = totalMonthEvents > 0 ? Math.round((attendedCount / totalMonthEvents) * 100) : 0

      setMonthStats({
        confirmed: confirmedCount,
        attended: attendedCount,
        missed: missedCount,
        declined: declinedCount,
        rate: monthRate,
        total: totalMonthEvents
      })

      setEvents(eventsWithStatus)

    } catch (error) {
      console.error('Load stats error:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    if (status === 'attended') {
      return (
        <span className="bg-[#eaf5ed] text-[#2b9646] px-3 py-1 rounded-full text-xs font-normal">
          Attended
        </span>
      )
    }
    if (status === 'missed') {
      return (
        <span className="bg-[#fff4ed] text-[#d97706] px-3 py-1 rounded-full text-xs font-normal">
          Missed
        </span>
      )
    }
    if (status === 'declined') {
      return (
        <span className="bg-[#fff4ed] text-[#d97706] px-3 py-1 rounded-full text-xs font-normal">
          Declined
        </span>
      )
    }
    if (status === 'confirmed') {
      return (
        <span className="bg-[#eaf5ed] text-[#2b9646] px-3 py-1 rounded-full text-xs font-normal">
          Confirmed
        </span>
      )
    }
    return (
      <span className="bg-slate-50 text-slate-500 px-3 py-1 rounded-full text-xs font-normal">
        No response
      </span>
    )
  }

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  if (loading) {
    return <LoadingSpinner message="Loading statistics..." />
  }

  return (
    <>
      <PullToRefresh onRefresh={loadStats}>
        <div className="bg-white min-h-screen pb-20">
          {/* Header */}
          <div className="bg-white px-6 pt-10 pb-4">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-semibold text-[#1E293B]">My Stats</h1>
              <button className="w-10 h-10 flex items-center justify-center bg-slate-50 border border-slate-200 rounded-[20px]">
                <Bell size={20} weight="fill" className="text-[#64748B]" />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="px-6 pb-6">
            <div className="flex gap-3">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-[15px] text-base text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3167fe]"
              >
                {months.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>

              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-[15px] text-base text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3167fe]"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 space-y-4">
            {/* Overall Attendance Rate Card */}
            <div 
              className="rounded-[25px] p-6 text-white relative overflow-hidden"
              style={{
                backgroundColor: '#2248B2'
              }}
            >
              {/* Pattern overlay */}
              <div 
                className="absolute inset-0 opacity-100"
                style={{
                  backgroundImage: 'url(/Line.png)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              ></div>

              {/* Content - needs to be above pattern */}
              <div className="relative z-10">
                <div className="absolute top-0 left-0">
                  <div className="">
                    <CalendarDots size={40} weight="fill" className="text-[#ADC2FF]" />
                  </div>
                </div>
                
                <div className="pt-20 pb-4 text-left">
                  <div className="text-7xl font-bold mb-3  text-[#D6E1FF] leading-none">{overallRate}%</div>
                  <p className="text-xl text-[#ADC2FF]">My overall<br/>attendance rate</p>
                </div>
              </div>
            </div>

            {/* Month Stats Card */}
            <div className="bg-white rounded-[20px] border border-slate-200 p-5">
              <h2 className="text-xl  text-[#475569] font-semibold text-slate-800 mb-4">
                Stats - {months[selectedMonth]}, {selectedYear}
              </h2>
              
              {/* Attendance rate */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-base text-[#1E293B] font-light ">Attendance rate</span>
                  <span className="text-base text-[#1E293B] font-bold ">{monthStats.rate}%</span>
                </div>
                
                <div className="w-full bg-[#EAF0FF] rounded-full h-6 mb-2">
                  <div 
                    className="bg-[#3167FE] h-6 rounded-full transition-all duration-500"
                    style={{ width: `${monthStats.rate}%` }}
                  ></div>
                </div>
                
                <p className="text-sm text-[#475569] mb-10" >
                  <span className="font-semibold text-[#1E293B]">{monthStats.attended} of {monthStats.total}</span> Total events
                </p>
              </div>

              {/* Stats list with colored dots */}
              <div className="space-y-3 mt-4">
                <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#3167fe]"></div>
                    <span className="text-sm text-[#475569]">Events Confirmed</span>
                  </div>
                  <span className="font-semibold text-[#1E293B]">{monthStats.confirmed}</span>
                </div>

                <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#2b9646]"></div>
                    <span className="text-sm text-[#475569]">Events Attended</span>
                  </div>
                  <span className="font-semibold text-[#1E293B]">{monthStats.attended}</span>
                </div>

                <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#F26D35]"></div>
                    <span className="text-sm text-[#475569]">Events Missed</span>
                  </div>
                  <span className="font-semibold text-[#1E293B]">{monthStats.missed}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#D00E0E]"></div>
                    <span className="text-sm text-[#475569]">Events Declined</span>
                  </div>
                  <span className="font-semibold text-[#1E293B]">{monthStats.declined}</span>
                </div>
              </div>
            </div>

            {/* Events List Card */}
            <div className="bg-white rounded-[20px] border border-slate-200 p-5">
              <h2 className="text-xl font-semibold text-[#475569] mb-4">
                Events - {months[selectedMonth]}, {selectedYear}
              </h2>
              
              {events.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">
                  No events in {months[selectedMonth]} {selectedYear}
                </p>
              ) : (
                <div className="space-y-3">
                  {events.map((event) => (
                    <div 
                      key={event.id}
                      className="flex items-center gap-3 py-3 border-b border-slate-100 last:border-0"
                    >
                      <div className="bg-slate-100 rounded-[12px] p-2 flex-shrink-0">
                        <CalendarBlank size={24} weight="fill" className="text-slate-600" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{event.title}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(event.start_date).toLocaleDateString('en-US', { 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </p>
                      </div>
                      
                      {getStatusBadge(event.status)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </PullToRefresh>
      
      <MemberBottomNav />
    </>
  )
}